if (!customElements.get('country-recommend-modal')) {
  const { Fancybox } = await import('@fancyapps/ui');
  class CountryRecommendModal extends HTMLElement {

    #debug = false;

    constructor() {
      super();
    }

    connectedCallback() {
      // Get the available locals
      this.locals = this.getLocals();
      if (!this.locals || this.locals.length === 0) return
      if (this.#debug) console.log('locals', this.locals);

      // Get the settings
      this.settings = this.getSettings();
      if (!this.settings) return;

      // Selectors
      this.popup = this.querySelector('[data-country-recommend-content]');

      // Validate pervious choices in the cookie
      const isoStorage = this.getIso()
      const isIsoValid = this.isIsoValid(isoStorage, this.settings.expired_time);
      if (this.#debug) console.log('isIsoValid', isIsoValid);

      // Return if the ISO is valid
      if (isIsoValid) return;

      // Get the suggestion data
      this.getSuggestionData().then((suggestionData) => {
        if (this.#debug) console.log('suggestionData', suggestionData);

        // Get the suggestion
        const suggestion = this.getSuggestion(suggestionData);
        if (!suggestion) {
          if (this.#debug) console.log('No suggestion found');
          return;
        }
        if (this.#debug) console.log('suggestion', suggestion);

        // Get the pathname
        const pathname = this.getPathname(suggestion);

        // Set the popup content
        this.setPopupContent(suggestion);

        // Open the modal
        setTimeout(() => {
          this.open(suggestion, pathname);
        }, 1000);
      });

      // Open the modal when the section is loaded
      if (Shopify.designMode) {
        window.addEventListener('shopify:section:load', (event) => {
          if (event.detail.sectionId === this.id) {
            this.open();
          }
        });
      }
    }

    /**
     * Get the locals
     * @returns {Array} - The locals
     */
    getLocals() {
      let locals = JSON.parse(this.dataset?.countryRecommendLocals || '[]');
      if (!locals || locals.length === 0) return null;
      locals = locals.map((local) => {
        return {
          ...local,
          language_code: local.language_code.toLowerCase(),
          country_code: local.country_code.toUpperCase()
        }
      });
      return locals;
    }

    /**
     * Get the settings
     * @returns {Object} - The settings
     */
    getSettings() {
      let settings = JSON.parse(this.dataset?.countryRecommendSettings || '{}');
      if (!settings) return null;
      settings.expired_time = settings.expired_time == undefined ? 30 : parseInt(settings.expired_time);
      settings.current_countries = (settings.current_countries || '').split(',');
      settings.current_locale = settings.current_locale || "en-US";
      settings.pathname = settings.pathname || '/';
      settings.custom_alternates = settings.custom_alternates || [];
      return settings;
    }

    /**
     * Get the suggestions
     * @returns
     */
    async getSuggestionData() {
      try {
        const response = await fetch(window.Shopify.routes.root + 'browsing_context_suggestions.json' + '?country[enabled]=true' + `&country[exclude]=${window.Shopify.country}` + '&language[enabled]=true' + `&language[exclude]=${window.Shopify.language}`);
        if (!response.ok) {
          throw new Error("Failed to fetch suggestions");
        }
        const data = await response.json();
        return data;
      } catch (error) {
        console.error("Error getting suggestions", error);
        return null;
      }
    }

    /**
     * Skip the suggestion
     * @param {String} country - The country code
     * @returns {Boolean} - True if the suggestion should be skipped, false otherwise
     */
    skipSuggestion(country) {
      if (!country) {
        return false;
      }
      if (window.location.hostname.startsWith('www.') && country == 'US') {
        return true;
      }
      if (!window.location.hostname.startsWith('www.') && this.settings.current_countries.includes(country)) {
        return true;
      }
      return false;
    }

    /**
     * Get the suggestion
     * @param {Object} suggestionData - The suggestion data
     * @returns {Object} - The suggestion
     */
    getSuggestion(suggestionData) {
      if (!suggestionData) return;

      const detectedValues = suggestionData.detected_values || {};
      const suggestions = suggestionData.suggestions || [];
      let suggestion = null;

      // Validate if we have a correct input
      if (!detectedValues.country && suggestions.length === 0) return null;

      // Match inside the same store with markets.
      for (const suggestionItem of suggestions) {
        const language = suggestionItem.parts?.language?.handle?.toLowerCase() || null
        const country = suggestionItem.parts?.country?.handle?.toUpperCase() || null
        if (!language || !country) continue;
        if (this.skipSuggestion(country)) continue;
        const local = this.locals.find((local) => local.country_code === country && local.language_code === language);
        if (!local) continue;
        suggestion = local;
        break; // Stop loop once suggestion is found
      }
      if (suggestion) return suggestion;

      // Match with on another store without markets with browser language
      suggestion = (function() {
        const country = detectedValues.country.handle.toUpperCase() || null;
        const language = navigator?.language?.split('-')[0].toLowerCase() || 'en';
        if (!country) return;
        if (this.skipSuggestion(country)) return;
        const local = this.locals.find((local) => local.country_code == country && local.language_code == language);
        if (!local) return;
        return local;
      }).call(this);
      if (suggestion) return suggestion;

      // Match with on another store without markets with default language (en)
      suggestion = (function() {
        const country = detectedValues.country.handle.toUpperCase() || null;
        const language = 'en';
        if (!country) return;
        if (this.skipSuggestion(country)) return;
        const local = this.locals.find((local) => local.country_code == country && local.language_code == language);
        if (!local) return;
        return local;
      }).call(this);
      if (suggestion) return suggestion;

      // No suggestion found
      return;
    }

    /**
     * Get the pathname
     * @param {Object} suggestion - The suggestion
     * @returns {String} - The pathname
     */
    getPathname(suggestion) {
      let pathname = this.settings.pathname || '/';
      if (!suggestion) {
        return pathname;
      }

      // Get the locale
      const locale = suggestion.language_code + '-' + suggestion.country_code;

      // Get the custom alternate pathname
      for (const customAlternate of this.settings.custom_alternates) {
        if (customAlternate.locale == locale) {
          pathname = customAlternate.pathname.startsWith('/') ? customAlternate.pathname : '/' + customAlternate.pathname;
          return pathname;
        }
      }

      return pathname;
    }

    /**
     * Set the popup content
     * @param {Object} suggestion - The suggestion
     * @returns
     */
    setPopupContent(suggestion) {
      if (!suggestion) return;

      try {
        const yesButton = this.popup.querySelector('[data-country-recommend-yes]');
        const title = suggestion.translation.replaceAll('[country]', suggestion.country);
        yesButton.innerHTML = `
          <img src="${suggestion.flag}" alt="${suggestion.country}" width="20" height="auto" loading="eager" class="inline-flex border border-theme-14 w-5 h-auto">
          <span class="max-md:hidden">
            ${title}
          </span>
          <span class="md:hidden">
            ${suggestion.country}
          </span>
        `;

        const description = this.popup.querySelector('[data-country-recommend-description]');
        description.innerHTML = description.innerHTML.replaceAll('[country]', `<strong>${suggestion.country}</strong>`);

        const descriptionMobile = this.popup.querySelector('[data-country-recommend-description-mobile]');
        descriptionMobile.innerHTML = descriptionMobile.innerHTML.replaceAll('[country]', `<strong>${suggestion.country}</strong>`);
      } catch (error) {
        console.error("Error setting popup content", error);
      }
    }

    /**
     * Open the country switcher modal
     * @param {Object} suggestion - The suggestion
     * @param {String} pathname - The pathname
     * @returns
     */
    open(suggestion, pathname) {
      if (!suggestion) return;
      new Fancybox(
        [
          {
            src: this.popup.innerHTML,
            type: 'html',
            autoFocus: false,
            showClass: false,
            fullscreen: {
              autoStart: true,
            },
          },
        ],
        {
          mainClass: 'country-recommend-modal',
          trapFocus: !this.isMobile,
          autoFocus: !this.isMobile,
          dragToClose: !this.isMobile,
          on: {
            done: (fancybox) => {
              if (!fancybox?.$container) return;
              this.fancyboxInstance = fancybox;
              this.fancybox = fancybox.$container;

              this.fancybox.querySelector('[data-country-recommend-yes]').addEventListener('click', (e) => {
                e.preventDefault();
                // Remove trailing slash from domain if present
                const domain = suggestion.domain.endsWith('/') ? suggestion.domain.slice(0, -1) : suggestion.domain;
                this.handleSwitch(
                  {
                    country_code: suggestion.country_code,
                    language_code: suggestion.language_code,
                    url: `${domain}${pathname}`,
                    useMarkets: suggestion.use_markets
                  }
                );
              });
              this.fancybox.querySelector('[data-country-recommend-no]').addEventListener('click', (e) => {
                e.preventDefault();
                this.fancyboxInstance.close();
              });
            },
            closing: () => {
              this.setIso(this.settings.current_locale);
            }
          },
        },
      );
    }

    /**
     * Handle the switch local action
     * @param {String} iso - The iso code of the language-Country (en-EN)
     * @param {String} url
     * @param {Boolean} useMarkets
     * @returns
     */
    handleSwitch({ country_code, language_code, url, useMarkets = false }) {
      if (!country_code || !language_code || !url || !this.fancybox) return;
      this.setIso(`${language_code}-${country_code}`);
      if (!useMarkets) {
        this.navigateToUrl(url);
      } else {
        const form = this.fancybox.querySelector('#country-recommend-form');
        form.querySelector('input[name="language_code"]').setAttribute('value', language_code);
        form.querySelector('input[name="country_code"]').setAttribute('value', country_code);
        form.querySelector('input[type="submit"]').click();
      }
    }

    /**
     * Get the previous choices from the cookie
     * @returns {Object} - The ISO storage
     * @example {iso: 'en-EN', timestamp: 1718000000}
     */
    getIso() {
      if (Shopify.designMode) return null;

      // Read from cookie instead of localStorage
      const cookies = document.cookie.split(';');
      for (let cookie of cookies) {
        const [name, value] = cookie.trim().split('=');
        if (name === 'preferred-locale') {
          try {
            return JSON.parse(decodeURIComponent(value));
          } catch (e) {
            console.error('Error parsing preferred-locale cookie', e);
            return null;
          }
        }
      }
      return null;
    }

    /**
     * Set the ISO storage
     * @param {String} iso - The iso code of the language-Country (en-EN)
     * @returns {Object} - The ISO storage
     * @example {iso: 'en-EN', timestamp: 1718000000}
     */
    setIso(iso) {
      if (!iso || Shopify.designMode) return;

      const storage = {
        iso: iso,
        timestamp: Date.now(),
      };

      // Use cookie instead of localStorage for cross-subdomain support
      const expires = new Date();
      expires.setTime(expires.getTime() + (365 * 24 * 60 * 60 * 1000)); // 1 year

      // Set cookie with domain=.alpinestars.com to work across all subdomains
      document.cookie = `preferred-locale=${encodeURIComponent(JSON.stringify(storage))}; expires=${expires.toUTCString()}; path=/; domain=.alpinestars.com`;

      return storage;
    }

    /**
     * Validate if the ISO storage is expired
     * @param {Object} isoStorage - The ISO storage {iso: 'en-EN', timestamp: 1718000000}
     * @param {Number} expiredTime - The expired time in days
     * @returns {Boolean}
     */
    isIsoValid(isoStorage, expiredTime = this.settings.expired_time) {
      if (Shopify.designMode) return true;
      if (!isoStorage) return false;

      // If we don't have the iso and timestamp, return false
      const iso = isoStorage?.iso;
      const timestamp = isoStorage?.timestamp;
      if (!iso || !timestamp) return false;

      // If the expired time is -1 (never expire), return true
      if (expiredTime === -1) return true;

      // Validate if the ISO storage is expired
      const expiredTimeInMilliseconds = expiredTime * 24 * 60 * 60 * 1000;
      const isExpired = timestamp < Date.now() - expiredTimeInMilliseconds;
      return !isExpired;
    }

    /**
     * Navigate to the url
     * @param {String} url - The url to navigate to
     * @returns
     */
    navigateToUrl(url) {
      window.location.href = url;
    }
  }

  window.CountryRecommendModal = CountryRecommendModal;

  customElements.define('country-recommend-modal', CountryRecommendModal);
}
